import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPut, apiDelete, apiGetPaginated } from '@/lib/api'
import { Waitlist, CreateWaitlistData, UpdateWaitlistData, Signup } from '@/types'

// Query keys
export const WAITLIST_QUERY_KEYS = {
  all: ['waitlists'] as const,
  lists: () => [...WAITLIST_QUERY_KEYS.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...WAITLIST_QUERY_KEYS.lists(), { filters }] as const,
  detail: (id: string) => [...WAITLIST_QUERY_KEYS.all, 'detail', id] as const,
  signups: (id: string) => [...WAITLIST_QUERY_KEYS.all, id, 'signups'] as const,
  analytics: (id: string) => [...WAITLIST_QUERY_KEYS.all, id, 'analytics'] as const,
}

// Get user's waitlists
export const useWaitlists = (params?: {
  page?: number
  per_page?: number
  status?: 'active' | 'paused' | 'archived'
}) => {
  return useQuery({
    queryKey: WAITLIST_QUERY_KEYS.list(params || {}),
    queryFn: async () => {
      const response = await apiGetPaginated<Waitlist>('/waitlists', params)
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch waitlists')
      }
      return response
    },
  })
}

// Get single waitlist
export const useWaitlist = (id: string) => {
  return useQuery({
    queryKey: WAITLIST_QUERY_KEYS.detail(id),
    queryFn: async () => {
      const response = await apiGet<Waitlist>(`/waitlists/${id}`)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch waitlist')
      }
      return response.data
    },
    enabled: !!id,
  })
}

// Get public waitlist (no auth required)
export const usePublicWaitlist = (slug: string) => {
  return useQuery({
    queryKey: ['public-waitlist', slug],
    queryFn: async () => {
      const response = await apiGet<Waitlist>(`/public/waitlists/${slug}`)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Waitlist not found')
      }
      return response.data
    },
    enabled: !!slug,
    retry: 1,
  })
}

// Create waitlist
export const useCreateWaitlist = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateWaitlistData) => {
      const response = await apiPost<Waitlist>('/waitlists', data)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create waitlist')
      }
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WAITLIST_QUERY_KEYS.lists() })
    },
  })
}

// Update waitlist
export const useUpdateWaitlist = (id: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateWaitlistData) => {
      const response = await apiPut<Waitlist>(`/waitlists/${id}`, data)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update waitlist')
      }
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(WAITLIST_QUERY_KEYS.detail(id), data)
      queryClient.invalidateQueries({ queryKey: WAITLIST_QUERY_KEYS.lists() })
    },
  })
}

// Delete waitlist
export const useDeleteWaitlist = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiDelete(`/waitlists/${id}`)
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete waitlist')
      }
      return true
    },
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: WAITLIST_QUERY_KEYS.detail(id) })
      queryClient.invalidateQueries({ queryKey: WAITLIST_QUERY_KEYS.lists() })
    },
  })
}

// Get waitlist signups
export const useWaitlistSignups = (
  waitlistId: string,
  params?: {
    page?: number
    per_page?: number
    search?: string
  }
) => {
  return useQuery({
    queryKey: [...WAITLIST_QUERY_KEYS.signups(waitlistId), params || {}],
    queryFn: async () => {
      const response = await apiGetPaginated<Signup>(
        `/waitlists/${waitlistId}/signups`,
        params
      )
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch signups')
      }
      return response
    },
    enabled: !!waitlistId,
  })
}

// Create signup (public endpoint)
export const useCreateSignup = (slug: string) => {
  return useMutation({
    mutationFn: async (data: {
      email: string
      name?: string
      company?: string
      recaptcha_token?: string | null
    }) => {
      const response = await apiPost<Signup>(`/public/waitlists/${slug}/signup`, data)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to join waitlist')
      }
      return response.data
    },
  })
}

// Delete signup
export const useDeleteSignup = (waitlistId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (signupId: string) => {
      const response = await apiDelete(`/waitlists/${waitlistId}/signups/${signupId}`)
      if (!response.success) {
        throw new Error(response.error || 'Failed to remove signup')
      }
      return true
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: WAITLIST_QUERY_KEYS.signups(waitlistId)
      })
    },
  })
}

// Get waitlist analytics
export const useWaitlistAnalytics = (
  waitlistId: string,
  params?: {
    period?: '7d' | '30d' | '90d' | '1y'
    timezone?: string
  }
) => {
  return useQuery({
    queryKey: [...WAITLIST_QUERY_KEYS.analytics(waitlistId), params || {}],
    queryFn: async () => {
      const response = await apiGet<{
        stats: {
          total_signups: number
          total_views: number
          conversion_rate: number
          growth_rate: number
        }
        daily_data: Array<{
          date: string
          signups: number
          views: number
          conversion_rate: number
        }>
        geographic_data?: Array<{
          country: string
          signups: number
          percentage: number
        }>
        referrer_data?: Array<{
          source: string
          signups: number
          percentage: number
        }>
      }>(`/waitlists/${waitlistId}/analytics`, { params })
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch analytics')
      }
      return response.data
    },
    enabled: !!waitlistId,
  })
}

// Export signups
export const useExportSignups = (waitlistId: string) => {
  return useMutation({
    mutationFn: async (format: 'csv' | 'json' = 'csv') => {
      const response = await apiGet<{ download_url: string }>(
        `/waitlists/${waitlistId}/export?format=${format}`
      )
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to export signups')
      }
      return response.data
    },
  })
}

// Export all analytics data
export const useExportAnalytics = () => {
  return useMutation({
    mutationFn: async () => {
      const response = await apiGet<{
        user: {
          id: string
          email: string
          name: string
          is_premium: boolean
        }
        waitlists: Array<{
          id: string
          name: string
          slug: string
          description: string
          created_at: string
          signups: Array<{
            id: string
            email: string
            name: string | null
            company: string | null
            position: number
            created_at: string
          }>
          signup_count: number
        }>
        export_timestamp: string
        total_waitlists: number
        total_signups: number
      }>('/analytics/export')

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to export analytics')
      }
      return response.data
    },
  })
}

// Export specific waitlist data
export const useExportWaitlist = () => {
  return useMutation({
    mutationFn: async (waitlistId: string) => {
      const response = await apiGet<{
        waitlist: {
          id: string
          name: string
          slug: string
          description: string
          created_at: string
        }
        signups: Array<{
          id: string
          email: string
          name: string | null
          company: string | null
          position: number
          created_at: string
        }>
        export_timestamp: string
        signup_count: number
      }>(`/waitlists/${waitlistId}/export`)

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to export waitlist')
      }
      return response.data
    },
  })
}

// Check slug availability
export const useCheckSlugAvailability = () => {
  return useMutation({
    mutationFn: async (slug: string) => {
      const response = await apiGet<{ available: boolean }>(
        `/waitlists/check-slug?slug=${encodeURIComponent(slug)}`
      )
      if (!response.success || response.data === undefined) {
        throw new Error(response.error || 'Failed to check slug availability')
      }
      return response.data.available
    },
  })
}